// Document upload form with drag-and-drop, customer selection, and metadata
import { useState, useCallback, useEffect } from 'react'
import { Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { D365_VERSIONS, D365_MODULES } from '@/lib/constants'
import { DOCUMENT_CATEGORIES } from '@/types/document'
import type { DocumentUploadFormData, DocumentCategory } from '@/types/document'
import type { Customer } from '@/types/customer'

interface DocumentUploadProps {
  onUpload: (data: DocumentUploadFormData) => Promise<unknown>
  isUploading: boolean
  error: Error | null
  customers: Customer[]
  customersLoading: boolean
  selectedCustomerId?: string
  onCustomerChange?: (customerId: string) => void
}

const ACCEPTED = '.txt,.md,.pdf,.docx,.msg,.png,.jpg,.jpeg'

export function DocumentUpload({
  onUpload,
  isUploading,
  error,
  customers,
  customersLoading,
  selectedCustomerId,
  onCustomerChange,
}: DocumentUploadProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [customerId, setCustomerId] = useState(selectedCustomerId || '')
  const [category, setCategory] = useState<DocumentCategory>('other')

  // Sync when parent changes the selected customer
  useEffect(() => {
    if (selectedCustomerId) setCustomerId(selectedCustomerId)
  }, [selectedCustomerId])
  const [description, setDescription] = useState('')
  const [d365Version, setD365Version] = useState('')
  const [d365Module, setD365Module] = useState('')
  const [tags, setTags] = useState('')
  const [saveToKB, setSaveToKB] = useState(true)
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) {
      setFile(dropped)
      if (!title) setTitle(dropped.name.replace(/\.[^.]+$/, ''))
    }
  }, [title])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) {
      setFile(selected)
      if (!title) setTitle(selected.name.replace(/\.[^.]+$/, ''))
    }
  }, [title])

  const handleCustomerChange = (value: string) => {
    setCustomerId(value)
    onCustomerChange?.(value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !title.trim() || !customerId) return

    await onUpload({
      file,
      title: title.trim(),
      category,
      customer_id: customerId,
      description: description.trim() || undefined,
      d365_version: d365Version || undefined,
      d365_module: d365Module || undefined,
      tags: tags.trim() || undefined,
      save_to_kb: saveToKB,
    })

    // Reset form (keep customer selection)
    setFile(null)
    setTitle('')
    setCategory('other')
    setDescription('')
    setD365Version('')
    setD365Module('')
    setTags('')
  }

  const activeCustomers = customers.filter((c) => c.is_active)

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Customer Selection */}
      <div className="space-y-1.5">
        <Label htmlFor="doc-customer">
          Customer <span className="text-red-500">*</span>
        </Label>
        <select
          id="doc-customer"
          value={customerId}
          onChange={(e) => handleCustomerChange(e.target.value)}
          disabled={customersLoading}
          required
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">
            {customersLoading ? 'Loading customers...' : '-- Select a customer --'}
          </option>
          {activeCustomers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.company_code ? ` (${c.company_code})` : ''}
            </option>
          ))}
        </select>
        {activeCustomers.length === 0 && !customersLoading && (
          <p className="text-xs text-muted-foreground">
            No customers found. Add customers in Settings first.
          </p>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          dragOver ? 'border-kaycoo-teal-dark bg-kaycoo-teal-pale/20' : 'border-gray-300',
          file ? 'border-green-400 bg-green-50' : ''
        )}
      >
        {file ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{file.name}</span>
            <span className="text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
            <button type="button" onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="mb-2 h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">Drag & drop a file or click to browse</p>
            <p className="text-xs text-gray-400">TXT, MD, PDF, DOCX, PNG, JPG (max 10 MB)</p>
            <input
              type="file"
              accept={ACCEPTED}
              onChange={handleFileSelect}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </>
        )}
      </div>

      {/* Metadata */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="doc-title">Title *</Label>
          <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="doc-category">Category</Label>
          <select
            id="doc-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {DOCUMENT_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="doc-version">D365 Version</Label>
          <select
            id="doc-version"
            value={d365Version}
            onChange={(e) => setD365Version(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Any version</option>
            {D365_VERSIONS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="doc-module">D365 Module</Label>
          <select
            id="doc-module"
            value={d365Module}
            onChange={(e) => setD365Module(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Any module</option>
            {D365_MODULES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="doc-desc">Description</Label>
          <Input id="doc-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="doc-tags">Tags (comma-separated)</Label>
          <Input id="doc-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. inventory, custom, v16" />
        </div>

        <div className="sm:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={saveToKB} onChange={(e) => setSaveToKB(e.target.checked)}
              className="h-4 w-4 rounded border-kaycoo-charcoal-2 text-kaycoo-teal-dark focus:ring-kaycoo-teal" />
            <span className="text-sm text-kaycoo-charcoal">Save to Knowledge Base</span>
            <span className="text-xs text-kaycoo-charcoal-2">(makes document searchable for issue resolution)</span>
          </label>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600">{error.message}</p>
      )}

      <Button
        type="submit"
        disabled={!file || !title.trim() || !customerId || isUploading}
        className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
      >
        {isUploading ? 'Uploading & Processing...' : 'Upload Document'}
      </Button>
    </form>
  )
}
